import { getSeoContent } from "./seoContent";

export type JsonLdNode = Record<string, unknown>;

export type PageSchemaInput = {
  title: string;
  description: string;
  canonical: string;
  pathname: string;
  image?: string;
};

export type ArticleSchemaInput = PageSchemaInput & {
  headline: string;
  author: string;
  datePublished: string | Date;
  dateModified?: string | Date | null;
};

const siteUrl = "https://variablesoft.com";
const organizationId = `${siteUrl}/#organization`;
const websiteId = `${siteUrl}/#website`;

const organization: JsonLdNode = {
  "@type": ["Organization", "ProfessionalService"],
  "@id": organizationId,
  name: "Variable Soft",
  legalName: "VARIABLE INFOTECH INDIA PRIVATE LIMITED",
  url: `${siteUrl}/`,
  logo: `${siteUrl}/logo.png`,
  description:
    "Variable Soft is a custom software development company in India helping businesses build CRM, ERP, mobile apps, web apps, AI automation, web scraping systems, IVR, dialer, and business workflow software.",
  email: "info@variablesoft.com",
  telephone: "+91 73000 75187",
  address: {
    "@type": "PostalAddress",
    streetAddress: "F-26, Vaishali Nagar",
    addressLocality: "Jaipur",
    addressRegion: "Rajasthan",
    postalCode: "302021",
    addressCountry: "IN",
  },
  sameAs: [
    "https://www.facebook.com/variablesoft",
    "https://x.com/variablesoft",
    "https://www.linkedin.com/company/variablesoft",
    "https://www.instagram.com/variable_soft/",
  ],
  contactPoint: [
    {
      "@type": "ContactPoint",
      telephone: "+91 73000 75187",
      contactType: "sales",
      areaServed: "IN",
      availableLanguage: ["en", "hi"],
    },
  ],
};

const website: JsonLdNode = {
  "@type": "WebSite",
  "@id": websiteId,
  url: `${siteUrl}/`,
  name: "Variable Soft",
  publisher: { "@id": organizationId },
};

function absoluteUrl(pathOrUrl?: string) {
  if (!pathOrUrl) return undefined;
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return `${siteUrl}${pathOrUrl.startsWith("/") ? "" : "/"}${pathOrUrl}`;
}

function serviceType(pathname: string, title: string) {
  if (pathname.includes("ai")) return "AI business automation and software development";
  if (pathname.includes("crm")) return "CRM development";
  if (pathname.includes("ivr") || pathname.includes("call-center") || pathname.includes("dialer")) return "Business communication software";
  if (pathname.includes("mobile")) return "Mobile app development";
  if (pathname.includes("ecommerce")) return "Ecommerce development";
  if (pathname.includes("webdesign")) return "Web design";
  if (pathname.includes("software-development")) return "Custom software development";
  return title.replace(" | Variable Soft", "");
}

function isServicePage(pathname: string) {
  return (
    pathname === "/service" ||
    pathname.startsWith("/services/") ||
    [
      "/webdesign",
      "/software-development-in-jaipur",
      "/ecommerce-development-services",
      "/top-software-companies-in-jaipur",
      "/it-companies-in-jaipur",
      "/maintenanceandsupport",
      "/cloud-call-center",
      "/ivr-system",
    ].includes(pathname)
  );
}

function softwareApplicationCategory(pathname: string) {
  if (pathname === "/crm" || pathname.startsWith("/crm/")) return "CRMApplication";
  if (pathname.includes("dialer") || pathname.includes("ivr")) return "CommunicationApplication";
  if (pathname.includes("taskmanagement")) return "ProductivityApplication";
  return "BusinessApplication";
}

function isSoftwarePage(pathname: string) {
  return pathname === "/crm" || pathname.startsWith("/products/") || pathname.startsWith("/crm/");
}

function faqNodes(pathname: string) {
  const routeContent = getSeoContent(pathname);
  if (routeContent?.faqs.length) {
    return routeContent.faqs.map((faq) => ({
      question: faq.question,
      answer: faq.answer,
    }));
  }

  if (pathname !== "/services/software-development") return undefined;

  return [
    {
      question: "What is custom software development?",
      answer:
        "Custom software development is the process of building software around a company's exact workflow, data, users and reporting needs instead of forcing the business to adapt to a ready-made tool.",
    },
    {
      question: "When should a business choose custom software?",
      answer:
        "Choose custom software when spreadsheets, generic SaaS products or disconnected tools create duplicate work, weak reporting, missed follow-ups or integration problems.",
    },
    {
      question: "Can Variable Soft modernize existing software?",
      answer:
        "Yes. Variable Soft can audit existing systems, improve UI, rebuild modules, add APIs, migrate data, strengthen hosting and add AI or automation features.",
    },
    {
      question: "Which industries use these systems?",
      answer:
        "Custom systems fit sales teams, real estate, education, ecommerce, service companies, call centers, finance operations and internal workflow teams.",
    },
  ];
}

function breadcrumbNodes(pathname: string) {
  if (pathname === "/") return undefined;

  const parts = pathname.split("/").filter(Boolean);
  const items = [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: `${siteUrl}/`,
    },
  ];

  parts.forEach((part, index) => {
    const path = `/${parts.slice(0, index + 1).join("/")}`;
    items.push({
      "@type": "ListItem",
      position: index + 2,
      name: part
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" "),
      item: `${siteUrl}${path}`,
    });
  });

  return {
    "@type": "BreadcrumbList",
    "@id": `${siteUrl}${pathname}#breadcrumb`,
    itemListElement: items,
  };
}

export function buildPageSchema(input: PageSchemaInput) {
  const graph: JsonLdNode[] = [
    organization,
    website,
    {
      "@type": "WebPage",
      "@id": `${input.canonical}#webpage`,
      url: input.canonical,
      name: input.title,
      description: input.description,
      isPartOf: { "@id": websiteId },
      about: { "@id": organizationId },
      primaryImageOfPage: input.image ? { "@id": `${absoluteUrl(input.image)}#primaryimage` } : undefined,
    },
  ];

  const breadcrumbs = breadcrumbNodes(input.pathname);
  if (breadcrumbs) graph.push(breadcrumbs);

  if (isServicePage(input.pathname)) {
    graph.push({
      "@type": "Service",
      "@id": `${input.canonical}#service`,
      name: input.title.replace(" | Variable Soft", ""),
      description: input.description,
      provider: { "@id": organizationId },
      areaServed: ["India", "Global"],
      serviceType: serviceType(input.pathname, input.title),
      url: input.canonical,
    });
  }

  if (isSoftwarePage(input.pathname)) {
    graph.push({
      "@type": "SoftwareApplication",
      "@id": `${input.canonical}#softwareapplication`,
      name: input.title.replace(" | Variable Soft", ""),
      applicationCategory: softwareApplicationCategory(input.pathname),
      operatingSystem: "Web",
      description: input.description,
      publisher: { "@id": organizationId },
      url: input.canonical,
    });
  }

  const faqs = faqNodes(input.pathname);
  if (faqs) {
    graph.push({
      "@type": "FAQPage",
      "@id": `${input.canonical}#faq`,
      mainEntity: faqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    });
  }

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  };
}

export function buildArticleSchema(input: ArticleSchemaInput) {
  return {
    "@context": "https://schema.org",
    "@graph": [
      organization,
      website,
      {
        "@type": "BlogPosting",
        "@id": `${input.canonical}#blogposting`,
        headline: input.headline,
        description: input.description,
        image: absoluteUrl(input.image),
        datePublished: new Date(input.datePublished).toISOString(),
        dateModified: new Date(input.dateModified || input.datePublished).toISOString(),
        author: {
          "@type": "Person",
          name: input.author,
        },
        publisher: { "@id": organizationId },
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": `${input.canonical}#webpage`,
        },
      },
      {
        "@type": "WebPage",
        "@id": `${input.canonical}#webpage`,
        url: input.canonical,
        name: input.title,
        description: input.description,
        isPartOf: { "@id": websiteId },
        about: { "@id": organizationId },
      },
      breadcrumbNodes(input.pathname),
    ].filter(Boolean),
  };
}
